// auto-generated by Typewriter template _models.tst from PortCall.EndPoints\Api\PortCallChange\ApiPortCallChange.cs
import S, { DataSignal } from 's-js';
import SArray, { SDataArray } from 's-array';
import TZDate from 'lib/TZDate';
import { jsonableObservable } from 'lib/jsonableObservable';
import { Barge, BargeData, BargeOptions } from 'data/models/Barge';
import { Berth, BerthData, BerthOptions } from 'data/models/Berth';
import { BerthReservation, BerthReservationData, BerthReservationOptions } from 'data/models/BerthReservation';
import { Event, EventData, EventOptions } from 'data/models/Event';
import { Measurement, MeasurementData, MeasurementOptions } from 'data/models/Measurement';
import { PCUser, PCUserData, PCUserOptions } from 'data/models/PCUser';
import { PortCall, PortCallData, PortCallOptions } from 'data/models/PortCall';
import { RecurringBatchChange, RecurringBatchChangeData, RecurringBatchChangeOptions } from 'data/models/RecurringBatchChange';
import { ScheduleDateEvent, ScheduleDateEventData, ScheduleDateEventOptions } from 'data/models/ScheduleDateEvent';
import { Vessel, VesselData, VesselOptions } from 'data/models/Vessel';
import { BerthReservationStatus, VesselSide } from 'server/enums/BerthReservation';
import { InvitedEditorPortCallChangeAction, LineTimeActualField, WatcherPortCallChangeAction } from 'server/enums/PortCallChange';
import { PortCallChangeType } from 'server/enums/PortCallChangeType';

// PortCallChangeData is the class that PortCallChange serializes to/from
export interface PortCallChangeData {
    Id : number;
	
    Berth : BerthData | null;
    BerthId : number;
    Event : EventData | null;
    EventId : number;
    InvitedEditor : PCUserData | null;
    InvitedEditorId : number;
    NewAgent : PCUserData | null;
    NewAgentId : number;
    NewBarge : BargeData | null;
    NewBargeId : number;
    NewBerth : BerthData | null;
    NewBerthId : number;
    NewVessel : VesselData | null;
    NewVesselId : number | null;
    OldBerth : BerthData | null;
    OldBerthId : number;
    OldVessel : VesselData | null;
    OldVesselId : number | null;
    PortCall : PortCallData | null;
    PortCallId : number;
    RecurringBatchChange : RecurringBatchChangeData | null;
    RecurringBatchChangeId : number | null;
    Reservation : BerthReservationData | null;
    ReservationId : number;
    ScheduleEvents : ScheduleDateEventData[] | null;
    Watcher : PCUserData | null;
    WatcherId : number;
	
    ActualTime : /* ISO8601 */ string;
    ApprovalCardTitle : string | null;
    EditorAction : InvitedEditorPortCallChangeAction;
    Field : LineTimeActualField;
    FirstLine : /* ISO8601 */ string;
    LastLine : /* ISO8601 */ string;
    NewFirstLine : /* ISO8601 */ string;
    NewFootmark : MeasurementData;
    NewLastLine : /* ISO8601 */ string;
    NewLength : MeasurementData;
    NewSide : VesselSide;
    NewStatus : BerthReservationStatus;
    OldFirstLine : /* ISO8601 */ string;
    OldFootmark : MeasurementData;
    OldLastLine : /* ISO8601 */ string;
    OldLength : MeasurementData;
    Type : PortCallChangeType;
    WatcherAction : WatcherPortCallChangeAction;
}

// PortCallChangeOptions is the class which may be passed to the PortCallChange constructor to define optional initial data values
export interface PortCallChangeOptions {
    Id : number;
	
    Berth? : BerthOptions | null;
    BerthId? : number;
    Event? : EventOptions | null;
    EventId? : number;
    InvitedEditor? : PCUserOptions | null;
    InvitedEditorId? : number;
    NewAgent? : PCUserOptions | null;
    NewAgentId? : number;
    NewBarge? : BargeOptions | null;
    NewBargeId? : number;
    NewBerth? : BerthOptions | null;
    NewBerthId? : number;
    NewVessel? : VesselOptions | null;
    NewVesselId? : number | null;
    OldBerth? : BerthOptions | null;
    OldBerthId? : number;
    OldVessel? : VesselOptions | null;
    OldVesselId? : number | null;
    PortCall? : PortCallOptions | null;
    PortCallId? : number;
    RecurringBatchChange? : RecurringBatchChangeOptions | null;
    RecurringBatchChangeId? : number | null;
    Reservation? : BerthReservationOptions | null;
    ReservationId? : number;
    ScheduleEvents? : ScheduleDateEventOptions[] | null;
    Watcher? : PCUserOptions | null;
    WatcherId? : number;
	
    ActualTime? : /* ISO8601 */ string;
    ApprovalCardTitle? : string | null;
    EditorAction? : InvitedEditorPortCallChangeAction;
    Field? : LineTimeActualField;
    FirstLine? : /* ISO8601 */ string;
    LastLine? : /* ISO8601 */ string;
    NewFirstLine? : /* ISO8601 */ string;
    NewFootmark? : MeasurementOptions;
    NewLastLine? : /* ISO8601 */ string;
    NewLength? : MeasurementOptions;
    NewSide? : VesselSide;
    NewStatus? : BerthReservationStatus;
    OldFirstLine? : /* ISO8601 */ string;
    OldFootmark? : MeasurementOptions;
    OldLastLine? : /* ISO8601 */ string;
    OldLength? : MeasurementOptions;
    Type? : PortCallChangeType;
    WatcherAction? : WatcherPortCallChangeAction;
}

export class PortCallChange {
    public Id : DataSignal<number>;
	
    public Berth : DataSignal<Berth | null>;
    public BerthId : DataSignal<number>;
    public Event : DataSignal<Event | null>;
    public EventId : DataSignal<number>;
    public InvitedEditor : DataSignal<PCUser | null>;
    public InvitedEditorId : DataSignal<number>;
    public NewAgent : DataSignal<PCUser | null>;
    public NewAgentId : DataSignal<number>;
    public NewBarge : DataSignal<Barge | null>;
    public NewBargeId : DataSignal<number>;
    public NewBerth : DataSignal<Berth | null>;
    public NewBerthId : DataSignal<number>;
    public NewVessel : DataSignal<Vessel | null>;
    public NewVesselId : DataSignal<number | null>;
    public OldBerth : DataSignal<Berth | null>;
    public OldBerthId : DataSignal<number>;
    public OldVessel : DataSignal<Vessel | null>;
    public OldVesselId : DataSignal<number | null>;
    public PortCall : DataSignal<PortCall | null>;
    public PortCallId : DataSignal<number>;
    public RecurringBatchChange : DataSignal<RecurringBatchChange | null>;
    public RecurringBatchChangeId : DataSignal<number | null>;
    public Reservation : DataSignal<BerthReservation | null>;
    public ReservationId : DataSignal<number>;
    public ScheduleEvents : SDataArray<ScheduleDateEvent>;
    public Watcher : DataSignal<PCUser | null>;
    public WatcherId : DataSignal<number>;
	
    public ActualTime : DataSignal<TZDate>;
    public ApprovalCardTitle : DataSignal<string | null>;
    public EditorAction : DataSignal<InvitedEditorPortCallChangeAction>;
    public Field : DataSignal<LineTimeActualField>;
    public FirstLine : DataSignal<TZDate>;
    public LastLine : DataSignal<TZDate>;
    public NewFirstLine : DataSignal<TZDate>;
    public NewFootmark : Measurement;
    public NewLastLine : DataSignal<TZDate>;
    public NewLength : Measurement;
    public NewSide : DataSignal<VesselSide>;
    public NewStatus : DataSignal<BerthReservationStatus>;
    public OldFirstLine : DataSignal<TZDate>;
    public OldFootmark : Measurement;
    public OldLastLine : DataSignal<TZDate>;
    public OldLength : Measurement;
    public Type : DataSignal<PortCallChangeType>;
    public WatcherAction : DataSignal<WatcherPortCallChangeAction>;

    constructor();
    constructor(data : PortCallChangeOptions);
    constructor(data: PortCallChangeOptions | null = null) {
        this.Id = jsonableObservable(S.value<number>(data && data.Id !== undefined ? data.Id : 0));
	    
        this.Berth = S.value<Berth | null>(null);
        this.BerthId = jsonableObservable(S.value<number>(data && data.BerthId !== undefined ? data.BerthId : 0));
        this.Event = S.value<Event | null>(null);
        this.EventId = jsonableObservable(S.value<number>(data && data.EventId !== undefined ? data.EventId : 0));
        this.InvitedEditor = S.value<PCUser | null>(null);
        this.InvitedEditorId = jsonableObservable(S.value<number>(data && data.InvitedEditorId !== undefined ? data.InvitedEditorId : 0));
        this.NewAgent = S.value<PCUser | null>(null);
        this.NewAgentId = jsonableObservable(S.value<number>(data && data.NewAgentId !== undefined ? data.NewAgentId : 0));
        this.NewBarge = S.value<Barge | null>(null);
        this.NewBargeId = jsonableObservable(S.value<number>(data && data.NewBargeId !== undefined ? data.NewBargeId : 0));
        this.NewBerth = S.value<Berth | null>(null);
        this.NewBerthId = jsonableObservable(S.value<number>(data && data.NewBerthId !== undefined ? data.NewBerthId : 0));
        this.NewVessel = S.value<Vessel | null>(null);
        this.NewVesselId = jsonableObservable(S.value<number | null>(data && data.NewVesselId !== undefined ? data.NewVesselId : null));
        this.OldBerth = S.value<Berth | null>(null);
        this.OldBerthId = jsonableObservable(S.value<number>(data && data.OldBerthId !== undefined ? data.OldBerthId : 0));
        this.OldVessel = S.value<Vessel | null>(null);
        this.OldVesselId = jsonableObservable(S.value<number | null>(data && data.OldVesselId !== undefined ? data.OldVesselId : null));
        this.PortCall = S.value<PortCall | null>(null);
        this.PortCallId = jsonableObservable(S.value<number>(data && data.PortCallId !== undefined ? data.PortCallId : 0));
        this.RecurringBatchChange = S.value<RecurringBatchChange | null>(null);
        this.RecurringBatchChangeId = jsonableObservable(S.value<number | null>(data && data.RecurringBatchChangeId !== undefined ? data.RecurringBatchChangeId : null));
        this.Reservation = S.value<BerthReservation | null>(null);
        this.ReservationId = jsonableObservable(S.value<number>(data && data.ReservationId !== undefined ? data.ReservationId : 0));
        this.ScheduleEvents = SArray<ScheduleDateEvent>([]);
        this.Watcher = S.value<PCUser | null>(null);
        this.WatcherId = jsonableObservable(S.value<number>(data && data.WatcherId !== undefined ? data.WatcherId : 0));
	    
        this.ActualTime = jsonableObservable(S.value<TZDate>(data && data.ActualTime !== undefined ? new TZDate(data.ActualTime) : new TZDate(0), TZDate.eq));
        this.ApprovalCardTitle = jsonableObservable(S.value<string | null>(data && data.ApprovalCardTitle !== undefined ? data.ApprovalCardTitle : null));
        this.EditorAction = jsonableObservable(S.value<InvitedEditorPortCallChangeAction>(data && data.EditorAction !== undefined ? data.EditorAction : 0));
        this.Field = jsonableObservable(S.value<LineTimeActualField>(data && data.Field !== undefined ? data.Field : 0));
        this.FirstLine = jsonableObservable(S.value<TZDate>(data && data.FirstLine !== undefined ? new TZDate(data.FirstLine) : new TZDate(0), TZDate.eq));
        this.LastLine = jsonableObservable(S.value<TZDate>(data && data.LastLine !== undefined ? new TZDate(data.LastLine) : new TZDate(0), TZDate.eq));
        this.NewFirstLine = jsonableObservable(S.value<TZDate>(data && data.NewFirstLine !== undefined ? new TZDate(data.NewFirstLine) : new TZDate(0), TZDate.eq));
        this.NewFootmark = data && data.NewFootmark !== undefined ? new Measurement(data.NewFootmark) : new Measurement();
        this.NewLastLine = jsonableObservable(S.value<TZDate>(data && data.NewLastLine !== undefined ? new TZDate(data.NewLastLine) : new TZDate(0), TZDate.eq));
        this.NewLength = data && data.NewLength !== undefined ? new Measurement(data.NewLength) : new Measurement();
        this.NewSide = jsonableObservable(S.value<VesselSide>(data && data.NewSide !== undefined ? data.NewSide : 0));
        this.NewStatus = jsonableObservable(S.value<BerthReservationStatus>(data && data.NewStatus !== undefined ? data.NewStatus : 0));
        this.OldFirstLine = jsonableObservable(S.value<TZDate>(data && data.OldFirstLine !== undefined ? new TZDate(data.OldFirstLine) : new TZDate(0), TZDate.eq));
        this.OldFootmark = data && data.OldFootmark !== undefined ? new Measurement(data.OldFootmark) : new Measurement();
        this.OldLastLine = jsonableObservable(S.value<TZDate>(data && data.OldLastLine !== undefined ? new TZDate(data.OldLastLine) : new TZDate(0), TZDate.eq));
        this.OldLength = data && data.OldLength !== undefined ? new Measurement(data.OldLength) : new Measurement();
        this.Type = jsonableObservable(S.value<PortCallChangeType>(data && data.Type !== undefined ? data.Type : 0));
        this.WatcherAction = jsonableObservable(S.value<WatcherPortCallChangeAction>(data && data.WatcherAction !== undefined ? data.WatcherAction : 0));
    }

    static key(obj : PortCallChange) {
        return obj.Id();
    }
    
    static dataKey(obj : PortCallChangeOptions) {
        return obj.Id;
    }

    static update(obj : PortCallChange, data : PortCallChangeOptions) {
        S.freeze(() => {
            if (data.Id !== undefined) obj.Id(data.Id);
	        
            if (data.BerthId !== undefined) obj.BerthId(data.BerthId);
            if (data.EventId !== undefined) obj.EventId(data.EventId);
            if (data.InvitedEditorId !== undefined) obj.InvitedEditorId(data.InvitedEditorId);
            if (data.NewAgentId !== undefined) obj.NewAgentId(data.NewAgentId);
            if (data.NewBargeId !== undefined) obj.NewBargeId(data.NewBargeId);
            if (data.NewBerthId !== undefined) obj.NewBerthId(data.NewBerthId);
            if (data.NewVesselId !== undefined) obj.NewVesselId(data.NewVesselId);
            if (data.OldBerthId !== undefined) obj.OldBerthId(data.OldBerthId);
            if (data.OldVesselId !== undefined) obj.OldVesselId(data.OldVesselId);
            if (data.PortCallId !== undefined) obj.PortCallId(data.PortCallId);
            if (data.RecurringBatchChangeId !== undefined) obj.RecurringBatchChangeId(data.RecurringBatchChangeId);
            if (data.ReservationId !== undefined) obj.ReservationId(data.ReservationId);
            if (data.WatcherId !== undefined) obj.WatcherId(data.WatcherId);
	        
            if (data.ActualTime !== undefined) obj.ActualTime(new TZDate(data.ActualTime));
            if (data.ApprovalCardTitle !== undefined) obj.ApprovalCardTitle(data.ApprovalCardTitle);
            if (data.EditorAction !== undefined) obj.EditorAction(data.EditorAction);
            if (data.Field !== undefined) obj.Field(data.Field);
            if (data.FirstLine !== undefined) obj.FirstLine(new TZDate(data.FirstLine));
            if (data.LastLine !== undefined) obj.LastLine(new TZDate(data.LastLine));
            if (data.NewFirstLine !== undefined) obj.NewFirstLine(new TZDate(data.NewFirstLine));
            if (data.NewFootmark !== undefined) Measurement.update(obj.NewFootmark, data.NewFootmark);
            if (data.NewLastLine !== undefined) obj.NewLastLine(new TZDate(data.NewLastLine));
            if (data.NewLength !== undefined) Measurement.update(obj.NewLength, data.NewLength);
            if (data.NewSide !== undefined) obj.NewSide(data.NewSide);
            if (data.NewStatus !== undefined) obj.NewStatus(data.NewStatus);
            if (data.OldFirstLine !== undefined) obj.OldFirstLine(new TZDate(data.OldFirstLine));
            if (data.OldFootmark !== undefined) Measurement.update(obj.OldFootmark, data.OldFootmark);
            if (data.OldLastLine !== undefined) obj.OldLastLine(new TZDate(data.OldLastLine));
            if (data.OldLength !== undefined) Measurement.update(obj.OldLength, data.OldLength);
            if (data.Type !== undefined) obj.Type(data.Type);
            if (data.WatcherAction !== undefined) obj.WatcherAction(data.WatcherAction);
        });
    }
}



// WEBPACK FOOTER //
// ./server/models/PortCallChange.ts